import React from 'react';
import { Grid, Hidden } from '@mui/material';
import { articleContentAreaStyles as classes } from '../styleObjects/ArticleContentArea';
import { RenderChips } from '../BlogSection/BlogComponents/Shared/RenderChips';
import { ArticleHeader } from '../ArticleHeader';
import { RenderedArticle } from '../Articles/RenderedArticle';
import { useAtom } from 'jotai';
import { isMobileAtom } from '@atoms/appSettings';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { TableOfContents } from '@components/TableOfContents/TableOfContents';
import { TableOfContentsMobile } from '@components/TableOfContents/TableOfContentsMobile';
import { Heading } from '@web-for-marketing/react-ui';
import { DownloadButton } from '@components/Articles/DownloadButton';
import { SuccessStoriesTranslation } from '@models/successStory';
import { SuccessStory } from '@models/successStory';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import { getYoutubeKey } from '@helpers/videoDriveKeyHelper';

interface SuccessStoryArticleCardProps {
    successStory: SuccessStory;
    successStoriesTranslation: SuccessStoriesTranslation;
}

export function SuccessStoryArticleCard({
    successStory,
    successStoriesTranslation,
}: SuccessStoryArticleCardProps): JSX.Element {
    const { mediaUrlPrefix } = useStaticValues();
    const [isMobile] = useAtom(isMobileAtom);
    const initialWidth = isMobile ? 'sm' : 'lg';

    return (
        <section css={classes.container}>
            <Grid container>
                <Grid item xs={12}>
                    <Heading variant='h1' color='primary' css={classes.title}>
                        {successStory.title}
                    </Heading>
                </Grid>
                {successStory.downloadUrl ? (
                    <div css={classes.downloadBtnWrapper}>
                        <DownloadButton
                            link={successStory.downloadUrl}
                            text={successStoriesTranslation.downloadSuccessStory}
                        />
                    </div>
                ) : null}
                {successStory.successStoryVideos && successStory.successStoryVideos.length ? (
                    <>
                        {successStory.successStoryVideos.map((video, index) => {
                            const youtubeKey = getYoutubeKey(video['url']);
                            return youtubeKey ? (
                                <div css={classes.videoMedia} key={index}>
                                    <LiteYouTubeEmbed id={youtubeKey} title={video.title} />
                                </div>
                            ) : (
                                <div css={classes.iframeMedia} key={index}>
                                    <iframe
                                        css={classes.responsiveIframe}
                                        title={video['title']}
                                        src={video['url']}
                                        frameBorder='0'
                                        allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
                                        allowFullScreen
                                    />
                                </div>
                            );
                        })}
                    </>
                ) : null}
                {successStory.imageUrl || successStory.videoUrl ? (
                    <Grid item xs={12} css={classes.media}>
                        <ArticleHeader article={successStory} />
                    </Grid>
                ) : null}
                <Grid item css={classes.content} xs={12}>
                    <Hidden initialWidth={initialWidth} mdDown>
                        <TableOfContents content={successStory.htmlContent} id='successstorytoc' />
                    </Hidden>
                    <Hidden initialWidth={initialWidth} mdUp>
                        <TableOfContentsMobile content={successStory.htmlContent} id='successstorytoc' />
                    </Hidden>
                    <Grid>
                        <RenderedArticle content={successStory.htmlContent} mediaUrlPrefix={mediaUrlPrefix} />
                    </Grid>
                </Grid>
                {successStory.tags && successStory.tags.length ? (
                    <Grid item css={classes.tags} xs={12}>
                        <RenderChips chips={successStory.tags} />
                    </Grid>
                ) : null}
            </Grid>
        </section>
    );
}
